import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalComponent from "..";
import {
  useAdminGetEventGuestQuery,
  useAdminResendTicketMutation,
  useAdminUpdateGuestDetailsMutation,
} from "../../../redux/features/admin/event/events.api";
import {
  useCheckGuestInMutation,
  useGetEventGuestQuery,
  useGetGuestCustomAnswersQuery,
  useResendTicketMutation,
} from "../../../redux/features/event/event.api";
import { GetEventGuestResponse } from "../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { PRIMARY_COLOR, PRODUCT_PRICING } from "../../../utils/constants";
import {
  formatDate,
  formatToCurrency,
  getCurrencyShort,
} from "../../../utils/helpers";
import { EventPermissions, ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { GuestCard } from "../../cards/guestCard";
import { ProfileIcon } from "../../cards/profilePic";
import { EmptyState } from "../../empty";
import { Icon } from "../../icon/icon";
import { LoadingOval } from "../../loaders/loaders";
import { Pill } from "../../pill";
import { TableComponent } from "../../table";
import { MiniHeader } from "../../typography/miniHeader";
import { useAppSelector } from "../../../redux/hooks";
import { toast } from "react-toastify";
import { TextInput } from "../../inputs/textInput";

const ResendTicketConfirmModal = ({
  open,
  handleClose,
  guestEmail,
  onConfirm,
  loading,
}: {
  open: boolean;
  handleClose: () => void;
  guestEmail: string;
  onConfirm: () => void;
  loading: boolean;
}) => {
  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      className="md:!max-w-[580px]"
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <Icon
        svg={"mail"}
        width={42}
        height={42}
        className="rounded-xl border border-grey-100 p-[11px] text-grey-700"
      />

      <div className="mb-4 mt-3">
        <MiniHeader
          header={"Resend Ticket"}
          subheader={
            <p className="text-sm text-grey-500">
              This would send the ticket to{" "}
              <span className="font-bold">{guestEmail}</span>. If this guest
              entered the wrong email,{" "}
              <a href="mailto:hello@usetikket.com" className="underline">
                send us a mail
              </a>{" "}
              or an{" "}
              <a
                href="https://instagram.com/usetikket"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                instagram DM @usetikket
              </a>
              .
            </p>
          }
        />
      </div>

      <div className="col-span-full flex w-full items-center justify-end gap-2">
        <Button
          type="button"
          onClick={handleClose}
          text="Cancel"
          variation="plain-grey"
          className="!max-w-fit"
          disabled={loading}
        />
        <Button
          type="button"
          onClick={onConfirm}
          text="Resend Ticket"
          variation="primary"
          className="!max-w-fit"
          loading={loading}
          disabled={loading}
        />
      </div>
    </ModalComponent>
  );
};

const EditGuestModal = ({
  open,
  handleClose,
  guest,
  guestId,
  onSuccess,
}: {
  open: boolean;
  handleClose: () => void;
  guest: GetEventGuestResponse | undefined;
  guestId: string;
  onSuccess: () => void;
}) => {
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });

  useEffect(() => {
    if (guest?.data) {
      setFormValues({
        firstName: guest.data.firstName ?? "",
        lastName: guest.data.lastName ?? "",
        email: guest.data.email ?? "",
        phoneNumber: guest.data.phoneNumber ?? "",
      });
    }
  }, [guest]);

  const [adminUpdateGuestDetails, { isLoading: updatingGuest }] =
    useAdminUpdateGuestDetailsMutation();

  const handleSave = async () => {
    const resp = await adminUpdateGuestDetails({
      guestId,
      payload: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        phoneNumber: formValues.phoneNumber || undefined,
      },
    });

    if ("data" in resp && resp.data?.status === "success") {
      onSuccess();
      handleClose();
    }
  };

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      containerClassName=""
      className="!max-w-[480px] !rounded-[16px] !bg-white !p-[24px]"
      dialogClassName=""
      showHeader={false}
      closeOnOverlayClick
      showCloseIcon={false}
    >
      <div className="flex flex-col gap-4">
        <p className="text-base font-bold text-primary">Edit User</p>

        <div className="grid grid-cols-2 gap-3">
          <TextInput
            type="text"
            hasTitle
            title="First Name"
            placeholder="Enter first name"
            value={formValues.firstName}
            onChange={(e) =>
              setFormValues({ ...formValues, firstName: e.target.value })
            }
            onBlur={() => {}}
            name="firstName"
          />
          <TextInput
            type="text"
            hasTitle
            title="Last Name"
            placeholder="Enter last name"
            value={formValues.lastName}
            onChange={(e) =>
              setFormValues({ ...formValues, lastName: e.target.value })
            }
            onBlur={() => {}}
            name="lastName"
          />
        </div>

        <TextInput
          type="email"
          hasTitle
          title="Email"
          placeholder="Enter email"
          value={formValues.email}
          onChange={(e) =>
            setFormValues({ ...formValues, email: e.target.value })
          }
          onBlur={() => {}}
          name="email"
        />

        <TextInput
          type="text"
          hasTitle
          title="Phone Number"
          placeholder="Enter phone number"
          value={formValues.phoneNumber}
          onChange={(e) =>
            setFormValues({ ...formValues, phoneNumber: e.target.value })
          }
          onBlur={() => {}}
          name="phoneNumber"
        />

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            onClick={handleClose}
            text="Cancel"
            variation="plain-grey"
            className="!min-w-fit"
            disabled={updatingGuest}
          />
          <Button
            type="button"
            onClick={handleSave}
            text="Save"
            variation="primary"
            className="!min-w-fit"
            loading={updatingGuest}
            disabled={updatingGuest}
          />
        </div>
      </div>
    </ModalComponent>
  );
};

export const ViewGuestModal = ({
  open,
  handleClose,
  guestId,
  adminView = false,
  eventCurrency,
  userEventPermissions,
}: ModalCompProps & {
  guestId: string;
  adminView?: boolean;
  eventCurrency: PayoutCurrency;
  userEventPermissions: EventPermissions[];
}) => {
  const [guest, setGuest] = useState<GetEventGuestResponse>();
  const [fetchingGuestDetails, setFetchingGuestDetails] =
    useState<boolean>(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [showEditGuest, setShowEditGuest] = useState(false);

  const { adminProfile } = useAppSelector((state) => state.auth);
  const isSuperAdmin = adminProfile?.role === "super-admin";

  const {
    data: clientGuest,
    isFetching: clientFetchingGuestDetails,
    refetch,
    isSuccess: guestDetailsSuccess,
  } = useGetEventGuestQuery(
    {
      id: guestId,
    },
    { refetchOnMountOrArgChange: true, skip: guestId === "" || adminView },
  );

  const [handleGuestCheckIn, { isLoading: checkingGuestIn }] =
    useCheckGuestInMutation();

  const { data: customAnswersResponse, isFetching: fetchingCustomAnswers } =
    useGetGuestCustomAnswersQuery(
      { guestId },
      { refetchOnMountOrArgChange: true, skip: guestId === "" || adminView },
    );

  const [resendTicket, { isLoading: resendingTicket }] =
    useResendTicketMutation();

  const [adminResendTicket, { isLoading: adminResendingTicket }] =
    useAdminResendTicketMutation();

  const navigate = useNavigate();

  const {
    data: adminGuest,
    isFetching: adminFetchingGuestDetails,
    isSuccess: adminnGuestDetailsSuccess,
    refetch: refetchAdminGuest,
  } = useAdminGetEventGuestQuery(
    {
      id: guestId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: guestId === "" || !adminView,
    },
  );

  useEffect(() => {
    if (adminView) {
      setFetchingGuestDetails(adminFetchingGuestDetails);
      setGuest(adminGuest);
    } else {
      setFetchingGuestDetails(clientFetchingGuestDetails);
      setGuest(clientGuest);
    }
  }, [
    clientGuest,
    clientFetchingGuestDetails,
    adminGuest,
    adminFetchingGuestDetails,
    adminView,
  ]);

  const handleResendConfirm = async () => {
    let resp: any;
    if (adminView) {
      resp = await adminResendTicket({ guestId });
    } else {
      resp = await resendTicket({ guestId });
    }

    if ("data" in resp && resp.data?.status === "success") {
      setShowResendConfirm(false);
      toast.success("Ticket Resent");
    }
  };

  const isResending = adminView ? adminResendingTicket : resendingTicket;

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        containerClassName="md:!py-[20px] md:!px-[20px] md:!justify-end"
        className="!max-h-[90%] !rounded-t-[16px] !bg-white !p-[20px] md:!max-h-[100%] md:!max-w-[500px] md:!rounded-[16px]"
        dialogClassName="!my-0"
        showHeader
        mobileAnchorBottom
        closeOnOverlayClick
        fullScreen
        showCloseIcon
        showMobileTopBar={false}
        footerChildren={
          ((!adminView && guestDetailsSuccess) ||
            (adminView && adminnGuestDetailsSuccess)) &&
          guest?.data?.eventId?.status !== "completed" ? (
            <button
              type="button"
              onClick={() => setShowResendConfirm(true)}
              className="w-full border-0 border-t border-grey-100 bg-transparent pt-3 text-center text-sm font-medium text-primary underline outline-none hover:opacity-70"
            >
              Resend Ticket
            </button>
          ) : undefined
        }
      >
        {fetchingGuestDetails ? (
          <div className="flex h-full w-full items-center justify-center">
            <LoadingOval color={PRIMARY_COLOR} />
          </div>
        ) : (
          <>
            {(!adminView && guestDetailsSuccess) ||
            (adminView && adminnGuestDetailsSuccess) ? (
              <div className="flex w-full flex-col gap-8">
                <div className="flex flex-col items-center justify-center gap-3 text-center">
                  <ProfileIcon dimension={64} img="" className="!p-[12px]" />

                  <div>
                    <p className="text-base font-bold tracking-tight text-grey-900 md:text-2xl">
                      {guest?.data?.firstName} {guest?.data?.lastName}
                    </p>
                    <p className="text-sm font-bold text-grey-500 md:text-base">
                      {guest?.data?.email}
                    </p>
                    {adminView && isSuperAdmin && guest?.data?.phoneNumber && (
                      <p className="text-sm font-medium text-blue">
                        {guest.data.phoneNumber}
                      </p>
                    )}
                  </div>

                  {guest?.data?.status === "checked-in" ? (
                    <p className="text-grey-500">
                      Checked in on{" "}
                      {formatDate(
                        guest?.data?.updatedAt,
                        "DD MMM, YYYY • h:mm A",
                      )}
                    </p>
                  ) : undefined}

                  {!adminView &&
                  userEventPermissions.includes("checkInGuests") ? (
                    <>
                      {guest?.data?.status === "unchecked-in" ? (
                        <Button
                          type="button"
                          onClick={async () => {
                            const resp = await handleGuestCheckIn({
                              id: guestId,
                            });

                            if (resp.data?.status === "success") {
                              refetch();
                            }
                          }}
                          text="Check In Guests"
                          icon="calendar-check-2"
                          variation="plain-grey"
                          className="!max-w-[272px]"
                          disabled={checkingGuestIn}
                          loading={checkingGuestIn}
                        />
                      ) : (
                        <Button
                          type="button"
                          onClick={() => {}}
                          text="Checked In"
                          icon="circle-check"
                          variation="green-outline"
                          className="!max-w-[272px] hover:!scale-100"
                        />
                      )}
                    </>
                  ) : undefined}

                  {adminView && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          navigate(`/users/${guest?.data?.userId}`);
                        }}
                        text="View User"
                        variation="plain-grey"
                        className="!max-w-[272px] hover:!scale-100"
                        icon="user"
                      />
                      <Button
                        type="button"
                        onClick={() => setShowEditGuest(true)}
                        text="Edit User"
                        variation="plain-grey"
                        className="!max-w-[272px] hover:!scale-100"
                        icon="pen"
                      />
                    </div>
                  )}
                </div>

                <TableComponent
                  headers={[]}
                  rows={[
                    {
                      id: 1,
                      items: [
                        <div className="flex flex-col gap-[2px]">
                          <div className="flex items-center justify-between gap-2 font-bold text-primary">
                            <p>
                              {guest?.data?.tickets?.reduce((acc, el) => {
                                return acc + el.quantity;
                              }, 0)}{" "}
                              {guest?.data?.tickets?.reduce((acc, el) => {
                                return acc + el.quantity;
                              }, 0) === 1
                                ? "Ticket"
                                : "Tickets"}
                            </p>

                            <div className="flex flex-col items-end gap-[2px]">
                              <p className="text-base font-bold">
                                {formatToCurrency(
                                  guest?.data?.payment?.amount ?? 0,
                                  { currency: getCurrencyShort(eventCurrency) },
                                )}
                              </p>
                              {guest?.data?.discount && (
                                <p className="text-sm text-grey-400 line-through">
                                  {formatToCurrency(
                                    guest?.data?.discount?.type === "amount"
                                      ? (guest?.data?.payment?.amount ?? 0) +
                                          (guest?.data?.discount?.value ?? 0)
                                      : (guest?.data?.payment?.amount ?? 0) /
                                          (1 -
                                            (guest?.data?.discount?.value ??
                                              0) /
                                              100),
                                    {
                                      currency: getCurrencyShort(eventCurrency),
                                    },
                                  )}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {guest?.data?.tickets?.map((t, idx) => {
                              return (
                                <Pill
                                  text={`${t.quantity}X ${t?.ticketId?.name}`}
                                  variation="grey"
                                  key={`${idx}-ticket`}
                                />
                              );
                            })}
                          </div>
                        </div>,
                      ],
                    },
                    {
                      id: 2,
                      items: [
                        guest?.data?.discount ? (
                          <div className="flex items-center gap-2">
                            <Icon
                              svg="ticket-percent"
                              width={24}
                              height={24}
                              className="text-blue"
                            />

                            <p className="flex-1 text-base font-bold uppercase text-primary">
                              {guest?.data?.discount?.name}
                            </p>

                            <p className="text-base text-grey-500">
                              {guest?.data?.discount?.type === "amount"
                                ? `${formatToCurrency(
                                    guest?.data?.discount?.value,
                                    {
                                      currency: getCurrencyShort(eventCurrency),
                                    },
                                  )} off`
                                : ""}
                              {guest?.data?.discount?.type === "percentage"
                                ? `${guest?.data?.discount?.value}% off`
                                : ""}
                            </p>
                          </div>
                        ) : undefined,
                      ],
                    },
                    {
                      id: 3,
                      items: [
                        <div className="flex flex-col gap-[2px]">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-grey-500">Our Fee</p>

                            <Pill
                              text={
                                guest?.data?.payment?.feeDescription ??
                                PRODUCT_PRICING[eventCurrency].feeText
                              }
                              variation="grey"
                            />
                          </div>

                          <p className="font-bold text-primary">
                            {formatToCurrency(guest?.data?.payment?.fees ?? 0, {
                              currency: getCurrencyShort(eventCurrency),
                            })}
                          </p>
                        </div>,
                      ],
                    },
                    {
                      id: 4,
                      items: [
                        <div>
                          <p className="text-base text-grey-500">
                            Date Registered
                          </p>
                          <p className="text-base font-bold text-primary">
                            {formatDate(
                              guest?.data.createdAt,
                              "MMMM DD, YYYY • h:mm A",
                            )}
                          </p>
                        </div>,
                      ],
                    },
                  ]}
                  onRowClick={(idx) => {}}
                  isRowClickable={false}
                  isTableEmpty={false}
                />

                {(fetchingCustomAnswers ||
                  (customAnswersResponse?.data?.length ?? 0) > 0) && (
                  <div className="flex flex-col gap-2">
                    <MiniHeader header="Registration Questions" />

                    {fetchingCustomAnswers ? (
                      <div className="h-[88px] animate-pulse rounded-2xl bg-grey-50" />
                    ) : (
                      <TableComponent
                        headers={[]}
                        rows={
                          customAnswersResponse?.data?.map((answer, idx) => {
                            return {
                              id: `${answer.questionId}-${idx}`,
                              items: [
                                <div className="flex flex-col gap-1">
                                  <p className="text-sm text-grey-500">
                                    {answer.question ?? "Question"}
                                  </p>
                                  <p className="text-base font-bold text-primary">
                                    {Array.isArray(answer.answer)
                                      ? answer.answer.join(", ")
                                      : answer.answer}
                                  </p>
                                </div>,
                              ],
                            };
                          }) ?? []
                        }
                        onRowClick={(idx) => {}}
                        isRowClickable={false}
                        isTableEmpty={false}
                      />
                    )}
                  </div>
                )}

                {guest !== undefined &&
                ![0, undefined].includes(guest?.data?.invitedGuests?.length) ? (
                  <div className="flex flex-col gap-2">
                    <MiniHeader header="Additional Guests" />

                    <TableComponent
                      headers={[]}
                      rows={guest.data.invitedGuests.map(
                        (invitedGuest, idx) => {
                          return {
                            id: idx,
                            items: [
                              <GuestCard
                                className=""
                                name={`${invitedGuest.firstName} ${invitedGuest.lastName}`}
                                email={`${invitedGuest.email}`}
                                timeAgo={undefined}
                                showTicket={false}
                                key={`invited-guests-row-${idx}`}
                              />,
                            ],
                          };
                        },
                      )}
                      onRowClick={(idx) => {}}
                      isRowClickable={false}
                      isTableEmpty={false}
                    />
                  </div>
                ) : undefined}
              </div>
            ) : (
              <EmptyState
                text="Guest not found"
                image="/assets/images/guest-glass.png"
              />
            )}
          </>
        )}
      </ModalComponent>

      <ResendTicketConfirmModal
        open={showResendConfirm}
        handleClose={() => setShowResendConfirm(false)}
        guestEmail={guest?.data?.email ?? ""}
        onConfirm={handleResendConfirm}
        loading={isResending}
      />

      {adminView && (
        <EditGuestModal
          open={showEditGuest}
          handleClose={() => setShowEditGuest(false)}
          guest={guest}
          guestId={guestId}
          onSuccess={() => {
            refetchAdminGuest();
          }}
        />
      )}
    </>
  );
};
